<template>
  <form
    class="main-cart critical-component-clear"
    :class="{'main-cart--empty': cart.item_count === 0}"
    action="/cart"
    method="post"
  >
    <div class="main-cart__content container col xs-span grid no-gap">
      <div
        class="
          main-cart__header
          col xs-span
          mb-xl mb-2xl-desktop
          "
      >
        <h1
         
          class="main-cart__title text-heading-xl"
          v-text="$string('cart.page_title')"
        />
        <span
          class="main-cart__header-count text-heading-xs ml-s"
          v-text="cart.item_count"
        />
      </div>

      <template v-if="cart.item_count > 0 || cartStatus.label === 'adding'">
        <div
          class="
              main-cart__line-items
              col xs-span l8
            "
        >
          <gift-with-purchase-trigger cart />
          <p
            v-if="specialMessage.enabled && specialMessage.text !== ''"
            class="text-body-m-regular main-cart__message"
            :class="`main-cart__message--${specialMessage.color}`"
            v-text="specialMessage.text"
          />

          <template
            v-for="(lineItem) of cartItems"
            :key="getCartItemKey(lineItem)"
          >
            <line-item
              class="main-cart__line-item"
              :line-item="lineItem"
              :liquid= "liquid"
            />

            <divider-line />
          </template>
        </div>
        <aside class="main-cart__sidebar col xs-span l9-12">
          <cart-summary
            class="main-cart__summary"
            :subtitle="subtitle"
            :usp-blocks="uspBlocks"
          />
        </aside>

        <cart-summary
          class="main-cart__summary main-cart__summary--fixed"
          :subtitle="subtitle"
          :usp-blocks="uspBlocks"
          sticky
        />
      </template>

      <template v-else>
        <div class="main-cart__empty">
          <p
            class="
            main-cart__empty-text
            text-body-l-regular
          "
            v-html="getTextHtml($string('cart.empty.text_html'))"
          />

          <div
            v-if="empty.ctaText && empty.ctaUrl"
            class="main-cart__empty-cta-container mt-2xl"
          >
            <btn
              class="main-cart__empty-cta"
              :label="empty.ctaText"
              :show-icon="false"
              :url="empty.ctaUrl"
            />
          </div>
        </div>
      </template>
    </div>

    <!--
      <div class="main-cart__header col xs-span l6">
      <h1

      class="main-cart__title text-heading-2xl"
      v-text="$string('cart.empty.title')"
      />

      <p
      class="
      main-cart__text
      text-body-m-regular
      text-body-l-regular-desktop
      "
      v-html="getTextHtml($string('cart.empty.text_html'))"
      />

      <div
      v-if="empty.ctaText && empty.ctaUrl"
      class="main-cart__empty-cta-container"
      >
      <btn
      class="main-cart__empty-cta"
      :label="empty.ctaText"
      :url="empty.ctaUrl"
      />
      </div>
      </div>
    -->
  </form>
</template>

<script>

/**
 * Vue: Main cart (main-cart)
 * -----------------------------------------------------------------------------
 * Cart page with line items.
 *
 * @param {Object} empty - Empty cart CTA settings.
 *
 */
import { mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import LineItem from '~async/line-item/line-item'
import GiftWithPurchaseTrigger from '~async/gift-with-purchase-trigger/gift-with-purchase-trigger'

export default {
  name: 'MainCart',

  components: {
    Btn,
    CartSummary,
    DividerLine,
    LineItem,
    GiftWithPurchaseTrigger,
  },

  props: {
    empty: {
      type: Object,
      default: () => ({
        ctaText: '',
        ctaUrl: '',
      }),
    },
    specialMessage: {
      type: Object,
      default: () => ({
        enabled: false,
        text: '',
      }),
    },
    subtitle: {
      type: String,
      default: '',
    },
    uspBlocks: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getGiftIdDismissed: 'gift-with-purchase/getIdDismissed',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
     cartItems() {
      let itemsToSort = this.cartStatus.label !== 'adding' ? this.cart.items : [...this.cart.items];
      
      const placeholders = [];

      this.cartStatus.items.forEach((addedItem) => {
        const match = itemsToSort.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id;

          return cartItemProperty === addedItem;
        });

        if (!match) {
          placeholders.push(false);
        }
      });

      itemsToSort = [...placeholders, ...itemsToSort];
      // Sort the items based on price
      itemsToSort.sort((a, b) => b.original_line_price - a.original_line_price);

      return itemsToSort;
    },
  },

  methods: {

    /**
     * Get text HTML.
     * @param {String} string - Text to format.
     * @returns {String}
     */
    getTextHtml(string) {
      return string
        .replace('\n', '<br>')
        .replaceAll('<a', '<a class="text-body-l-regular"')
    },

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    getCartItemKey(item) {
      if (!item) {
        return 'cart-line-item-loading'
      }

      return `cart-line-item-${item.key}`
    },
  },
}
</script>

<style lang="scss">
@import './main-cart';
</style>
